import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useRequirementsMobileLayout from '../../../hooks/useRequirementsMobileLayout';
import {
  computeAccountRequiredFortnightly,
} from '../../../utils/requirementCalculations';
import BankAccountRequirementCard from './BankAccountRequirementCard';
import BankAccountRequirementSheet from './BankAccountRequirementSheet';
import BankAccountRequirementDetail, { sortRequirements } from './BankAccountRequirementDetail';

/**
 * Bank-account grouped requirements: desktop accordion + mobile bottom sheet (Phase 2).
 */
const BankAccountRequirementsView = ({
  displayRequirements,
  filteredRequirements,
  fortnightStart,
  fortnightEnd,
  fortnightIndex,
  showInactive,
  onShowInactiveChange,
  expandedBankAccounts,
  onToggleAccount,
  onEdit,
  onAdvancedGenerate,
  onViewBills,
  getCategoryIcon,
  onSheetOpenChange,
}) => {
  const isMobile = useRequirementsMobileLayout();
  const [sheetAccountKey, setSheetAccountKey] = useState(null);

  useEffect(() => {
    onSheetOpenChange?.(Boolean(sheetAccountKey));
  }, [sheetAccountKey, onSheetOpenChange]);

  const accountGroups = useMemo(() => {
    const meta = {};
    displayRequirements.forEach((req) => {
      const key = req.bank_account != null ? String(req.bank_account) : 'none';
      if (!meta[key]) {
        const details = req.bank_account_details;
        meta[key] = {
          accountKey: key,
          title: details
            ? `${details.nickname} · ${details.bank}`
            : 'No bank account',
          order: details?.order ?? 9999,
          nickname: details?.nickname || '',
        };
      }
    });

    const byAccount = {};
    filteredRequirements.forEach((req) => {
      const key = req.bank_account != null ? String(req.bank_account) : 'none';
      if (!byAccount[key]) {
        byAccount[key] = { ...meta[key], requirements: [] };
      }
      byAccount[key].requirements.push(req);
    });

    return Object.values(byAccount)
      .map((g) => {
        const alloc = [];
        const bill = [];
        g.requirements.forEach((req) => {
          if (req.display_kind === 'allocation') {
            alloc.push(req);
          } else {
            bill.push(req);
          }
        });
        return {
          ...g,
          allocations: sortRequirements(alloc),
          bills: sortRequirements(bill),
        };
      })
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return (a.nickname || '').localeCompare(b.nickname || '');
      });
  }, [displayRequirements, filteredRequirements]);

  const accountColumns = useMemo(() => {
    const left = [];
    const right = [];
    accountGroups.forEach((group, index) => {
      if (index % 2 === 0) {
        left.push(group);
      } else {
        right.push(group);
      }
    });
    return [left, right];
  }, [accountGroups]);

  const sheetGroup = useMemo(
    () => accountGroups.find((g) => g.accountKey === sheetAccountKey) || null,
    [accountGroups, sheetAccountKey]
  );

  const sheetRequiredFn = useMemo(() => {
    if (!sheetGroup || !fortnightStart || !fortnightEnd) return 0;
    return computeAccountRequiredFortnightly(
      sheetGroup.requirements,
      fortnightStart,
      fortnightEnd
    );
  }, [sheetGroup, fortnightStart, fortnightEnd]);

  const renderCategoryIcon = (iconName, color) => (
    <FontAwesomeIcon
      icon={getCategoryIcon(iconName)}
      style={{ color: color || '#6c757d', fontSize: '1em' }}
    />
  );

  const renderCard = (group) => (
    <BankAccountRequirementCard
      key={group.accountKey}
      accountKey={group.accountKey}
      title={group.title}
      requirements={group.requirements}
      fortnightStart={fortnightStart}
      fortnightEnd={fortnightEnd}
      fortnightIndex={fortnightIndex}
      showInactive={showInactive}
      isExpanded={expandedBankAccounts[group.accountKey] === true}
      isMobile={isMobile}
      onToggle={() => onToggleAccount(group.accountKey)}
      onOpenSheet={setSheetAccountKey}
      onEdit={onEdit}
      onAdvancedGenerate={onAdvancedGenerate}
      onViewBills={onViewBills}
      onShowInactiveChange={onShowInactiveChange}
      renderCategoryIcon={renderCategoryIcon}
    />
  );

  if (!accountGroups.length) {
    return (
      <p className="text-center text-muted py-4">
        No requirements for this fortnight. Try &quot;Show inactive&quot; or another fortnight.
      </p>
    );
  }

  return (
    <>
      <div className={`requirements-bank-grid${isMobile ? ' requirements-bank-grid--mobile' : ''}`}>
        {isMobile ? (
          accountGroups.map((group) => renderCard(group))
        ) : (
          accountColumns.map((column, columnIndex) => (
            <div key={columnIndex === 0 ? 'left' : 'right'} className="requirements-bank-grid-column">
              {column.map((group) => renderCard(group))}
            </div>
          ))
        )}
      </div>

      {isMobile && sheetGroup && fortnightStart && fortnightEnd && (
        <BankAccountRequirementSheet
          show
          title={sheetGroup.title}
          requiredFn={sheetRequiredFn}
          onClose={() => setSheetAccountKey(null)}
        >
          <BankAccountRequirementDetail
            allocations={sheetGroup.allocations}
            bills={sheetGroup.bills}
            requirements={sheetGroup.requirements}
            fortnightStart={fortnightStart}
            fortnightEnd={fortnightEnd}
            fortnightIndex={fortnightIndex}
            showInactive={showInactive}
            onShowInactiveChange={onShowInactiveChange}
            onEdit={onEdit}
            onAdvancedGenerate={onAdvancedGenerate}
            onViewBills={onViewBills}
            renderCategoryIcon={renderCategoryIcon}
          />
        </BankAccountRequirementSheet>
      )}
    </>
  );
};

BankAccountRequirementsView.propTypes = {
  displayRequirements: PropTypes.arrayOf(PropTypes.object).isRequired,
  filteredRequirements: PropTypes.arrayOf(PropTypes.object).isRequired,
  fortnightStart: PropTypes.instanceOf(Date),
  fortnightEnd: PropTypes.instanceOf(Date),
  fortnightIndex: PropTypes.number.isRequired,
  showInactive: PropTypes.bool.isRequired,
  onShowInactiveChange: PropTypes.func.isRequired,
  expandedBankAccounts: PropTypes.object.isRequired,
  onToggleAccount: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAdvancedGenerate: PropTypes.func.isRequired,
  onViewBills: PropTypes.func.isRequired,
  getCategoryIcon: PropTypes.func.isRequired,
  onSheetOpenChange: PropTypes.func,
};

export default BankAccountRequirementsView;
